import { emptySplitApi } from "./empty-split-api";
import {
  buildOpportunitiesUrl,
  buildSalesReviewsUrl,
  type OpportunityAppointmentsQueryArgs,
  type SalesReviewsQueryArgs
} from "./sales-coach";
import type {
  AnalyseResponse,
  OpportunityAppointmentsResponse,
  SalesReview,
  SalesReviewStatus,
  SalesReviewsResponse
} from "./types/sales-coach.type";

/**
 * Sales Coach v2 (SN-19967): the v1 review and opportunity routes, served under the v2
 * scoring rules at `/api/coach/v2/*`. Same payloads plus the v2 fields, same tags, and the
 * same options as the v1 twins in `services/sales-coach.ts` — read the comments there.
 *
 * Never import these hooks directly: go through `features/sales-coach/hooks/use-sales-coach-api.hook.ts`,
 * which picks v1 or v2 from `useSalesCoachVersion()`.
 *
 * What differs from v1, and why it matters here:
 * - Every route 404s when the backend switch `SALES_COACH_V2_ENABLED` is off.
 * - The detail, status and mark-read routes accept a v1 OR a v2 id, and answer with the row
 *   the v2 UI shows: the fresh v2 sibling (derived on read if needed), or the v1 row with
 *   `derivation.status` saying why there is no fresh sibling. So the id you ask for and the
 *   `id` you get back can differ.
 * - The opportunity list falls back to the v1 state and the v1 id, with no score, when a
 *   visit has no fresh sibling. The review list serves fresh v2 rows only.
 */
const V2_BASE = "/coach/v2";

export const salesCoachV2Api = emptySplitApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getOpportunityAppointmentsV2: builder.query<
      OpportunityAppointmentsResponse,
      OpportunityAppointmentsQueryArgs
    >({
      query: (args) => buildOpportunitiesUrl(args, V2_BASE),
      providesTags: [{ type: "SalesCoachOpportunities" as const, id: "LIST" }]
    }),

    /**
     * Answers with the sibling's id once the v1 run is finished and derivable, else the v1 id.
     *
     * No refetch on a 404: RTK invalidates a mutation's tags on a rejected call too, and with the
     * v2 switch off every list route 404s as well (the controller deliberately does not invalidate
     * on it either). The v1 twin keeps its fixed tags: there a 404 means the row is gone.
     */
    analyseOpportunityV2: builder.mutation<AnalyseResponse, number>({
      query: (appointmentId) => ({
        url: `${V2_BASE}/opportunity-appointments/${appointmentId}/analyse/`,
        method: "POST",
        timeout: 30_000
      }),
      invalidatesTags: (result, error) =>
        error && "status" in error && error.status === 404
          ? []
          : [{ type: "SalesCoachOpportunities" as const, id: "LIST" }]
    }),

    /**
     * Re-analyse (PRD D25): a real, paid v2 run of one visit, replacing the review worked out from v1
     * under the same id. A visit that already has its own v2 run gets that run back, unchanged; a
     * refusal is a 409 whose `detail` is the sentence to show.
     *
     * Invalidates every review entry and both lists, so an open review window and the rows flip to
     * running — except on a 404, which with the backend switch off would only refetch dead routes.
     */
    reanalyseOpportunityV2: builder.mutation<AnalyseResponse, number>({
      query: (appointmentId) => ({
        url: `${V2_BASE}/opportunity-appointments/${appointmentId}/reanalyse/`,
        method: "POST",
        timeout: 30_000
      }),
      invalidatesTags: (result, error) =>
        error && "status" in error && error.status === 404
          ? []
          : [
              "SalesCoachReviews" as const,
              { type: "SalesCoachOpportunities" as const, id: "LIST" }
            ]
    }),

    /** Untagged and `keepUnusedDataFor: 0`, for the reasons on the v1 twin. */
    getSalesReviewStatusV2: builder.query<SalesReviewStatus, number>({
      query: (reviewId) => `${V2_BASE}/sales-reviews/${reviewId}/status/`,
      keepUnusedDataFor: 0
    }),

    /**
     * Tagged with the id asked for AND the id served, so a mark-read on either one
     * refetches this entry.
     */
    getSalesReviewV2: builder.query<SalesReview, number>({
      query: (reviewId) => `${V2_BASE}/sales-reviews/${reviewId}/`,
      providesTags: (result, error, reviewId) => [
        { type: "SalesCoachReviews" as const, id: reviewId },
        ...(result && result.id !== reviewId
          ? [{ type: "SalesCoachReviews" as const, id: result.id }]
          : [])
      ]
    }),

    /** Marks the row the v2 UI shows: the sibling, deriving it first if needed. */
    markSalesReviewReadV2: builder.mutation<void, number>({
      query: (reviewId) => ({
        url: `${V2_BASE}/sales-reviews/${reviewId}/mark-read/`,
        method: "POST"
      }),
      invalidatesTags: (result, error, reviewId) => [
        { type: "SalesCoachOpportunities" as const, id: "LIST" },
        { type: "SalesCoachReviews" as const, id: "LIST" },
        { type: "SalesCoachReviews" as const, id: reviewId }
      ]
    }),

    getSalesReviewsV2: builder.query<
      SalesReviewsResponse,
      SalesReviewsQueryArgs
    >({
      query: (args) => buildSalesReviewsUrl(args, V2_BASE),
      providesTags: [{ type: "SalesCoachReviews" as const, id: "LIST" }]
    })
  })
});

export const {
  useGetOpportunityAppointmentsV2Query,
  useAnalyseOpportunityV2Mutation,
  useReanalyseOpportunityV2Mutation,
  useGetSalesReviewStatusV2Query,
  useLazyGetSalesReviewStatusV2Query,
  useGetSalesReviewV2Query,
  useMarkSalesReviewReadV2Mutation,
  useGetSalesReviewsV2Query
} = salesCoachV2Api;
